<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { fromHtml as fromHtmlPage } from '@/routes/tools/pdf';
import fromHtmlTool from '@/routes/tools/pdf/from-html';

const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.from_html.seo',
    fallbackTitleKey: 'tools.pdf.from_html.title',
    fallbackTitleLabel: 'HTML to PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: fromHtmlPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.from_html.title',
                title: 'HTML to PDF',
                href: fromHtmlPage.url(),
            },
        ],
    },
});

type InputMode = 'file' | 'paste';
type Status = 'idle' | 'loading' | 'success' | 'error';

const inputMode = ref<InputMode>('file');
const file = ref<File | null>(null);
const htmlContent = ref('');
const status = ref<Status>('idle');
const statusMessage = ref('');

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
}

function switchMode(mode: InputMode): void {
    inputMode.value = mode;
    file.value = null;
    htmlContent.value = '';
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.conversion_failed', 'Conversion failed.')
            );
        } catch {
            return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
        }
    }

    return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
}

async function convert(): Promise<void> {
    if (status.value === 'loading') {
        return;
    }

    if (inputMode.value === 'file' && !file.value) {
        return;
    }

    if (inputMode.value === 'paste' && !htmlContent.value.trim()) {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.converting_to_pdf',
        'Converting to PDF...',
    );

    const formData = new FormData();

    if (inputMode.value === 'file' && file.value) {
        formData.append('file', file.value);
    } else {
        formData.append('html_content', htmlContent.value);
    }

    try {
        const response = await axios.post(fromHtmlTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'converted.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.converted_success',
            'Converted successfully!',
        );
        file.value = null;
        htmlContent.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

const canConvert = (): boolean => {
    if (inputMode.value === 'file') {
        return !!file.value;
    }

    return htmlContent.value.trim().length > 0;
};
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-from-html" />

    <ToolLayout
        :title="t('tools.pdf.from_html.title', 'HTML to PDF')"
        :subtitle="t('tools.pdf.from_html.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div class="mb-4 flex gap-2">
                    <button
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                        :class="
                            inputMode === 'file'
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="switchMode('file')"
                    >
                        {{
                            t(
                                'tools.pdf.ui.html_upload_tab',
                                'Upload HTML file',
                            )
                        }}
                    </button>
                    <button
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                        :class="
                            inputMode === 'paste'
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="switchMode('paste')"
                    >
                        {{ t('tools.pdf.ui.html_paste_tab', 'Paste HTML') }}
                    </button>
                </div>

                <div v-if="inputMode === 'file'">
                    <DropZone
                        accept=".html,.htm,text/html"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_html_file',
                                'Click or drag HTML file here',
                            )
                        "
                        :sublabel="
                            t(
                                'tools.pdf.ui.html_extensions',
                                '.html and .htm files · max 10 MB',
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-4">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <div v-else>
                    <label
                        class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{
                            t('tools.pdf.ui.html_content_label', 'HTML content')
                        }}
                    </label>
                    <textarea
                        v-model="htmlContent"
                        rows="12"
                        placeholder="<html><body><h1>Hello World</h1></body></html>"
                        class="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                    />
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!canConvert() || status === 'loading'"
                @click="convert"
            >
                {{ t('tools.sections.to_pdf', 'Convert to PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
